import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Button } from '../Button'
import { IconButton } from '../IconButton'
import { TextFieldNew } from '../TextField'
import { SANS_SERIF_FONT_FAMILY, SERIF_FONT_FAMILY } from '../styles'
import { EmailVerification } from './EmailVerification'

const useStyles = createUseStyles({
  title: {
    fontFamily: SERIF_FONT_FAMILY,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(144, 115, 79, 0.8)',
    marginLeft: 30,
    marginBottom: 40,
  },
  passwordRequirements: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginBottom: 18,
    color: 'rgba(54, 52, 50, 0.8)',
  },
  passwordRequirementsError: {
    color: '#F57561',
  },
})

interface Props {
  onBack: () => void
  email?: string
}

export const RecoverPassword: FC<Props> = flowMax(
  addDisplayName('RecoverPassword'),
  addState('showEmailMessage', 'setShowEmailMessage', false),
  addState('email', 'onChangeEmail', ({ email }) => email ?? ''),
  addNavigate,
  addApi,
  addHandlers({
    onClick:
      ({ api, setShowEmailMessage, email }) =>
      () => {
        api.auth.resetPasswordEmail({ email, source: 'creatorWeb' })
        setShowEmailMessage(true)
      },
  }),
  addProps(({ email }) => ({
    buttonDisabled: email === '',
  })),
  addHandlers({
    onKeyPress:
      ({ onClick, buttonDisabled }) =>
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !buttonDisabled) {
          onClick()
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    email,
    onChangeEmail,
    onBack,
    onClick,
    onKeyPress,
    showEmailMessage,
    buttonDisabled,
    classes,
    t,
  }) => (
    <div>
      <IconButton name="backRodeo" onClick={onBack} />
      {showEmailMessage && email ? (
        <EmailVerification
          email={email}
          emailType="passwordReset"
          onBack={onBack}
        />
      ) : (
        <>
          <h1 className={classes.title}>{t('auth.recoverPassword.title')}</h1>
          <p className={classes.subtitle}>
            {t('auth.recoverPassword.subtitle')}
          </p>
          <TextFieldNew
            id="email"
            label={t('auth.fields.email')}
            value={email ?? ''}
            onChange={onChangeEmail}
            showClearInput={false}
            onKeyPress={onKeyPress}
          />
          <Button
            text={t('auth.recoverPassword.button')}
            type="fullWidth"
            onClick={onClick}
            disabled={buttonDisabled}
          />
        </>
      )}
    </div>
  )
)
